import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { ShippingTargetDialogState } from '../shipping-target-dialog.state';

/** Position „Ziel-Kunde": Kundensuche inklusive Rücksendeadress-Prüfung. */
@Component({
  standalone: true,
  selector: 'hawk-shipping-customer-position',
  imports: [FormsModule, RouterLink, FontAwesomeModule],
  template: `
    @if (state.targetCustomerId(); as customerId) {
      <div
        class="bm-alert bm-alert-soft flex items-center"
        [class.bm-alert-success]="state.targetCustomerHasReturn()"
        [class.bm-alert-error]="!state.targetCustomerHasReturn()"
      >
        <fa-icon [icon]="state.targetCustomerHasReturn() ? iconCheck : iconTimes" class="mr-2" />
        <div class="min-w-0">
          <strong>{{ state.targetCustomerName() }}</strong>
          @if (state.targetCustomerHasReturn()) {
            — Rücksendeadresse hinterlegt
          } @else {
            — keine Rücksendeadresse hinterlegt
            <a [routerLink]="['/kunde', customerId]" target="_blank" class="g-link underline ml-1">
              beim Kunden hinterlegen ↗
            </a>
          }
        </div>
        @if (!state.readOnly()) {
          <button type="button" class="bm-btn bm-btn-ghost bm-btn-xs ml-auto" (click)="state.clearCustomer()">
            Ändern
          </button>
        }
      </div>
    } @else {
      <div class="relative">
        <input
          type="text"
          class="bm-input w-full"
          placeholder="Kunde suchen…"
          [disabled]="state.readOnly()"
          [(ngModel)]="search"
          (ngModelChange)="state.searchCustomers($event)"
        />
        @if (state.showCustomerDropdown() && state.customerResults().length > 0) {
          <div
            class="absolute z-20 mt-1 w-full bg-base-100 border border-base-200 rounded-lg shadow-lg max-h-60 overflow-auto"
          >
            @for (customer of state.customerResults(); track customer.id) {
              <button
                type="button"
                class="block w-full text-left px-3 py-2 hover:bg-base-200 text-sm"
                (click)="pick(customer)"
              >
                {{ customer.name }}
                @if (!customer.has_return_address) {
                  <span class="text-error text-xs">(keine Rücksendeadresse)</span>
                }
              </button>
            }
          </div>
        }
      </div>

      @if (state.context()?.project_is_internal) {
        <span class="text-warning text-xs mt-1 block">
          Internes Projekt: bitte einen Ziel-Kunden mit Rücksendeadresse wählen.
        </span>
      } @else if (state.context()?.customer_has_return_address) {
        <span class="text-xs opacity-60 mt-1 block">
          Ohne Auswahl geht die Rücksendung an den Projekt-Kunden
          @if (state.context()?.customer_name) {
            <strong>{{ state.context()?.customer_name }}</strong>
          }.
        </span>
      } @else {
        <div class="bm-alert bm-alert-error bm-alert-soft mt-1">
          <fa-icon [icon]="iconTimes" class="mr-2" />
          <div>
            Der Projekt-Kunde hat keine Rücksendeadresse — bitte einen Ziel-Kunden wählen
            @if (state.context()?.customer_id; as projectCustomerId) {
              oder
              <a [routerLink]="['/kunde', projectCustomerId]" target="_blank" class="g-link underline">
                die Rücksendeadresse hinterlegen ↗
              </a>
            }.
          </div>
        </div>
      }
    }
  `,
})
export class ShippingCustomerPositionComponent {
  protected state = inject(ShippingTargetDialogState);
  protected iconCheck = faCheckCircle;
  protected iconTimes = faTimesCircle;
  protected search = '';

  protected pick(customer: { id: number; name: string; has_return_address: boolean }): void {
    this.state.selectCustomer(customer);
    this.search = '';
  }
}
